import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Search, Check, X } from 'lucide-react';
import { financialStatementApi } from '../../services/financialStatementApi';
import type { AccountMapping, UnmappedAccount, LineConfig } from '../../types/financialStatement';
import { useToast } from '../ui/Toast';

interface Props {
    company: string;
    year: number;
    canEdit: boolean;
}

export function AccountMappingTab({ company, year, canEdit }: Props) {
    const { toast } = useToast();
    const [mappings, setMappings] = useState<AccountMapping[]>([]);
    const [unmapped, setUnmapped] = useState<UnmappedAccount[]>([]);
    const [lines, setLines] = useState<LineConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [editingNr, setEditingNr] = useState<string | null>(null);
    const [editDebit, setEditDebit] = useState(0);
    const [editCredit, setEditCredit] = useState(0);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [mappingResult, unmappedResult, linesResult] = await Promise.all([
                financialStatementApi.getAccountMappings(company),
                financialStatementApi.getUnmappedAccounts(company, year),
                financialStatementApi.getLinesConfig(),
            ]);
            setMappings(mappingResult.mappings);
            setUnmapped(unmappedResult.unmappedAccounts);
            setLines(linesResult.lines);
        } catch (err) {
            console.error('Failed to load account mappings:', err);
        } finally {
            setLoading(false);
        }
    }, [company, year]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleSave = async (accountNr: string) => {
        try {
            await financialStatementApi.updateAccountMapping(accountNr, {
                companyCode: company,
                lineNrDebit: editDebit,
                lineNrCredit: editCredit,
            });
            setEditingNr(null);
            await loadData();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Save failed', 'error');
        }
    };

    const filtered = mappings.filter(m =>
        !search || m.AccountNr.includes(search) || m.AccountName.toLowerCase().includes(search.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Unmapped accounts warning */}
            {unmapped.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="h-5 w-5 text-amber-600" />
                        <h3 className="font-semibold text-amber-800">
                            {unmapped.length} Unmapped Account{unmapped.length !== 1 ? 's' : ''}
                        </h3>
                    </div>
                    <p className="text-sm text-amber-700 mb-3">
                        These accounts have postings in {year} but no mapping to a financial statement line.
                        Their amounts are missing from the report.
                    </p>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="border-b border-amber-200">
                                    <th className="px-3 py-1 text-left font-medium text-amber-700">Account</th>
                                    <th className="px-3 py-1 text-left font-medium text-amber-700">Name</th>
                                    <th className="px-3 py-1 text-right font-medium text-amber-700">Amount</th>
                                    {canEdit && <th className="px-2 py-1 w-20"></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {unmapped.map(acc => (
                                    <tr key={acc.AccountNr} className="border-b border-amber-100">
                                        <td className="px-3 py-1 font-mono">{acc.AccountNr}</td>
                                        <td className="px-3 py-1">{acc.AccountName}</td>
                                        <td className="px-3 py-1 text-right tabular-nums">
                                            {new Intl.NumberFormat('de-CH', { minimumFractionDigits: 0 }).format(Math.round(acc.AmountLCY))}
                                        </td>
                                        {canEdit && (
                                            <td className="px-2 py-1">
                                                {editingNr === acc.AccountNr ? (
                                                    <div className="flex items-center gap-1">
                                                        <button onClick={() => handleSave(acc.AccountNr)} className="text-green-600 hover:text-green-800 p-1"><Check className="h-4 w-4" /></button>
                                                        <button onClick={() => setEditingNr(null)} className="text-gray-400 hover:text-gray-600 p-1"><X className="h-4 w-4" /></button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => {
                                                            setEditingNr(acc.AccountNr);
                                                            setEditDebit(0);
                                                            setEditCredit(0);
                                                        }}
                                                        className="text-xs text-amber-700 hover:text-amber-900 underline"
                                                    >
                                                        Map
                                                    </button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                ))}
                                {/* Inline editing row for unmapped account */}
                                {editingNr && unmapped.some(a => a.AccountNr === editingNr) && (
                                    <tr className="bg-amber-100 border-b border-amber-200">
                                        <td colSpan={canEdit ? 4 : 3} className="px-3 py-2">
                                            <div className="flex items-center gap-4 text-sm">
                                                <span className="font-medium text-amber-800">
                                                    Assign {editingNr} to line:
                                                </span>
                                                <label className="flex items-center gap-1">
                                                    <span className="text-amber-700">Debit</span>
                                                    <select value={editDebit} onChange={e => setEditDebit(parseInt(e.target.value))} className="rounded border border-amber-300 px-2 py-1 text-sm">
                                                        <option value={0}>Select...</option>
                                                        {lines.map(l => <option key={l.LineNr} value={l.LineNr}>{l.LineNr} - {l.LineNameEnglish}</option>)}
                                                    </select>
                                                </label>
                                                <label className="flex items-center gap-1">
                                                    <span className="text-amber-700">Credit</span>
                                                    <select value={editCredit} onChange={e => setEditCredit(parseInt(e.target.value))} className="rounded border border-amber-300 px-2 py-1 text-sm">
                                                        <option value={0}>Select...</option>
                                                        {lines.map(l => <option key={l.LineNr} value={l.LineNr}>{l.LineNr} - {l.LineNameEnglish}</option>)}
                                                    </select>
                                                </label>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search accounts..."
                    className="w-full rounded-md border border-gray-300 pl-9 pr-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
            </div>

            {/* Mapping table */}
            <div className="overflow-x-auto">
                <table className="min-w-full">
                    <thead>
                        <tr className="border-b-2 border-gray-200">
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Account</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-28">Line Debit</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Debit Line Name</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-28">Line Credit</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Credit Line Name</th>
                            {canEdit && <th className="px-2 py-2 w-16"></th>}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filtered.map(m => (
                            <tr key={m.AccountNr} className={editingNr === m.AccountNr ? 'bg-yellow-50' : 'hover:bg-gray-50'}>
                                <td className="px-3 py-1.5 text-sm font-mono">{m.AccountNr}</td>
                                <td className="px-3 py-1.5 text-sm">{m.AccountName}</td>
                                <td className="px-3 py-1.5 text-sm">
                                    {editingNr === m.AccountNr ? (
                                        <select value={editDebit} onChange={e => setEditDebit(parseInt(e.target.value))} className="w-full rounded border px-2 py-1 text-sm">
                                            <option value={0}>Select...</option>
                                            {lines.map(l => <option key={l.LineNr} value={l.LineNr}>{l.LineNr} - {l.LineNameEnglish}</option>)}
                                        </select>
                                    ) : m.LineNrDebit}
                                </td>
                                <td className="px-3 py-1.5 text-sm text-gray-500">
                                    {editingNr === m.AccountNr ? '' : m.DebitLineName}
                                </td>
                                <td className="px-3 py-1.5 text-sm">
                                    {editingNr === m.AccountNr ? (
                                        <select value={editCredit} onChange={e => setEditCredit(parseInt(e.target.value))} className="w-full rounded border px-2 py-1 text-sm">
                                            <option value={0}>Select...</option>
                                            {lines.map(l => <option key={l.LineNr} value={l.LineNr}>{l.LineNr} - {l.LineNameEnglish}</option>)}
                                        </select>
                                    ) : m.LineNrCredit}
                                </td>
                                <td className="px-3 py-1.5 text-sm text-gray-500">
                                    {editingNr === m.AccountNr ? '' : m.CreditLineName}
                                </td>
                                {canEdit && (
                                    <td className="px-2 py-1.5">
                                        {editingNr === m.AccountNr ? (
                                            <div className="flex gap-1">
                                                <button onClick={() => handleSave(m.AccountNr)} className="text-green-600 hover:text-green-800 p-1"><Check className="h-4 w-4" /></button>
                                                <button onClick={() => setEditingNr(null)} className="text-gray-400 hover:text-gray-600 p-1"><X className="h-4 w-4" /></button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => {
                                                    setEditingNr(m.AccountNr);
                                                    setEditDebit(m.LineNrDebit);
                                                    setEditCredit(m.LineNrCredit);
                                                }}
                                                className="text-gray-400 hover:text-blue-600 p-1"
                                            >
                                                <span className="text-xs underline">Edit</span>
                                            </button>
                                        )}
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <p className="text-xs text-gray-400">
                {filtered.length} of {mappings.length} mappings shown | {company.toUpperCase()}
            </p>
        </div>
    );
}
